/**
 * The model asking the nurse something, mid-dictation.
 *
 * These arrive only at the seam between bottles — never while she is still
 * describing one — and only about something she ACTUALLY SAID that the model
 * could not resolve: a name it could not place, a strength it heard as either
 * forty or fourteen, a schedule it could not separate. Never about a field she
 * left out, because an unspoken field is correctly empty and asking would turn
 * her silence into an answer she never gave. That restraint lives in the
 * model's prompt; what lives here is the promise that a question is cheap to
 * ignore.
 *
 * She answers by TALKING. There is no reply control on these cards and there
 * should not be: her voice is already streaming into the session that asked, so
 * the model hears the answer the way it hears everything else. The (x) is not
 * "dismiss without answering" — it is "get this off my screen", which she can do
 * at any point, answered or not.
 *
 * The timing rules are deliberate and shared:
 *
 *   - At most THREE on screen, NEWEST AT TOP. This is a display limit and
 *     nothing more — the model asks as often as there are unclear bottles, and
 *     a fourth question is never suppressed, it just pushes the oldest card off
 *     the bottom of the stack.
 *
 *     Newest-at-top because the chime is what brings her eyes to the phone, and
 *     they land at the top: the anchor of the stack and the start of reading
 *     order. Putting the newest card at the bottom means the sound points her
 *     at a card she has already read. Appending at the bottom would keep the
 *     existing cards from shifting, which is the real argument for it — but
 *     questions arrive at bottle boundaries, seconds apart, so that shift is
 *     rare and gentle, while being pointed at the wrong card happens every time.
 *   - ONE timer for the whole stack, not one each. A new question restarts it
 *     and they all leave together. Staggered timers would make cards vanish
 *     mid-read at unpredictable moments; a single clock means the stack is
 *     either settling or it is not.
 *   - THREE seconds of quiet ends it. Long enough to read one sentence, short
 *     enough that it never becomes furniture.
 *
 * Sound and haptic both fire on arrival because she is looking at a bottle, not
 * at the phone. Both are non-fatal: a device with no haptics and a bundle with
 * no sound asset both just mean she sees the card without a cue.
 */
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { createAudioPlayer } from "expo-audio";
import * as Haptics from "expo-haptics";
import { useCallback, useEffect, useRef, useState } from "react";
import { Animated, Pressable, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";
import { useWebSocketEvent } from "@/lib/websocket";

/**
 * Cards on screen at once. Purely a display limit — a fourth question is still
 * asked and still delivered, it just evicts the oldest card off the bottom.
 */
const MAX_VISIBLE = 3;

/** Quiet time before the whole stack leaves, restarted by each new arrival. */
const DISMISS_AFTER_MS = 3000;

/** Exit animation for an evicted card. Kept short so the stack never lags her. */
const EXIT_MS = 160;

/**
 * Cross-platform on purpose.
 *
 * An earlier pass used LayoutAnimation so the surviving cards would slide when
 * one arrives or leaves, instead of repositioning instantly. It was dropped:
 * it is a native-only API that no-ops on the web build, and this feature has no
 * business needing one. Each card animates its own arrival and departure with
 * `Animated`, which behaves the same everywhere; the cards that merely move
 * take their new positions directly.
 *
 * If the reposition ever reads as too abrupt, the fix is a measured, hand-rolled
 * transform on each card — not a native layout API. It would be a deliberate
 * piece of work, not a one-line import.
 */

/**
 * Two rising notes, 260 ms, peaking at -14 dBFS.
 *
 * Synthesised rather than sourced — see scripts/make-notification-sound.ts for
 * the design and the reasoning, including why it is deliberately quiet and
 * deliberately not alarm-like: a nurse hears this in someone's home with the
 * patient in the room.
 *
 * Loaded lazily and defensively anyway. The haptic below is the cue that always
 * works on a handset, and a dictation must never fail over a chime.
 */
let questionSound: ReturnType<typeof createAudioPlayer> | null = null;
let soundLoadAttempted = false;

function playArrivalCue(): void {
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning).catch(() => {
        /* no haptics on web and on some Android builds — the sound carries it */
    });

    if (!soundLoadAttempted) {
        soundLoadAttempted = true;
        try {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            questionSound = createAudioPlayer(require("@/assets/sounds/dictation-question.wav"));
        } catch {
            questionSound = null; // no asset resolved — haptic only
        }
    }
    try {
        if (questionSound) {
            questionSound.seekTo(0);
            questionSound.play();
        }
    } catch {
        /* never let a chime take down a capture */
    }
}

interface QuestionToast {
    /** True while this card is animating off the bottom; removed when done. */
    leaving?: boolean;
    id: string;
    question: string;
    kind: "repeat" | "disambiguate";
    field: "name" | "strength" | "orderedDose" | "frequency" | "administrationNotes" | "route";
    options: string[] | null;
    aboutMedication: string | null;
}

/**
 * What the question is about, for the card's eyebrow line.
 *
 * The model only ever asks about a detail she SPOKE, so this always names
 * something she actually said — it is a pointer back into her own sentence,
 * not a prompt to supply something new.
 */
const FIELD_LABEL: Record<QuestionToast["field"], string> = {
    name: "name",
    strength: "strength",
    orderedDose: "dose",
    frequency: "frequency",
    administrationNotes: "instructions",
    route: "route"
};

export function DictationQuestionToasts({ visitId }: { visitId: string }) {
    const [toasts, setToasts] = useState<QuestionToast[]>([]);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    /**
     * One clock for the stack: restarted on arrival, and on fire it marks every
     * card as leaving rather than dropping them.
     *
     * Marking, not clearing, is what makes the stack fade rather than blink out
     * of existence — and it routes the group dismissal through the same exit
     * path as an eviction and as her (x), so there is one teardown to get right
     * instead of three.
     */
    const restartTimer = useCallback(() => {
        clearTimer();
        timer.current = setTimeout(() => {
            timer.current = null;
            setToasts((current) => current.map((t) => ({ ...t, leaving: true })));
        }, DISMISS_AFTER_MS);
    }, [clearTimer]);

    /**
     * While she is touching a card she is reading it, so the clock stops.
     *
     * Counted rather than a boolean: she can put a second finger on a second
     * card, and a plain flag would let the first release restart a timer the
     * second finger is still holding.
     */
    const holds = useRef(0);
    const onHoldStart = useCallback(() => {
        holds.current += 1;
        clearTimer();
    }, [clearTimer]);
    const onHoldEnd = useCallback(() => {
        holds.current = Math.max(0, holds.current - 1);
        if (holds.current === 0) {
            // A full three seconds from letting go, not the remainder — she has
            // just finished reading, and the countdown she half-watched before
            // picking the card up is not the one that should apply now.
            restartTimer();
        }
    }, [restartTimer]);

    useWebSocketEvent("medication-dictation:question", (data) => {
        if (String(data.visitId) !== visitId) {
            return;
        }
        setToasts((current) => {
            // A redelivered broadcast must not chime twice or evict a card she
            // is mid-way through reading.
            if (current.some((t) => t.id === data.questionId)) {
                return current;
            }
            // Newest first: the chime sends her eyes to the top of the stack.
            const next: QuestionToast[] = [
                {
                    id: data.questionId,
                    question: data.question,
                    kind: data.kind,
                    field: data.field,
                    options: data.options,
                    aboutMedication: data.aboutMedication
                },
                ...current
            ];

            // Over the limit: mark the oldest as leaving rather than dropping it
            // on the spot, so it animates off the bottom instead of vanishing.
            const live = next.filter((t) => !t.leaving);
            if (live.length > MAX_VISIBLE) {
                const evicted = live[live.length - 1];
                return next.map((t) => (t.id === evicted.id ? { ...t, leaving: true } : t));
            }
            return next;
        });
        playArrivalCue();
        // A question landing while she is holding a card does not start the
        // clock; her finger still owns it, and onHoldEnd starts it on release.
        if (holds.current === 0) {
            restartTimer();
        }
    });

    /** Unmount a card once its exit animation has finished. */
    const remove = useCallback(
        (id: string) => {
            setToasts((current) => {
                const next = current.filter((t) => t.id !== id);
                if (next.length === 0) {
                    clearTimer();
                }
                return next;
            });
        },
        [clearTimer]
    );

    /** Her (x). Animates out the same way an evicted card does. */
    const dismiss = useCallback((id: string) => {
        setToasts((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    }, []);

    useEffect(() => clearTimer, [clearTimer]);

    if (toasts.length === 0) {
        return null;
    }

    return (
        // pointerEvents="box-none" so the stack never blocks the list behind it —
        // only the cards themselves take touches, and only on their (x).
        <View style={ss.stack} pointerEvents="box-none">
            {toasts.map((toast) => (
                <QuestionCard
                    key={toast.id}
                    toast={toast}
                    onDismiss={() => dismiss(toast.id)}
                    onExited={() => remove(toast.id)}
                    onHoldStart={onHoldStart}
                    onHoldEnd={onHoldEnd}
                />
            ))}
        </View>
    );
}

function QuestionCard({
    toast,
    onDismiss,
    onExited,
    onHoldStart,
    onHoldEnd
}: {
    toast: QuestionToast;
    onDismiss: () => void;
    onExited: () => void;
    onHoldStart: () => void;
    onHoldEnd: () => void;
}) {
    // 0 = off-stack, 1 = seated. Drives entry and exit from one value, so a card
    // evicted mid-entry reverses cleanly instead of fighting a second animation.
    const seat = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(seat, {
            toValue: 1,
            duration: 180,
            useNativeDriver: true
        }).start();
    }, [seat]);

    // `onExited` is a fresh closure on every render of the parent. Held through
    // a ref so the exit effect below can depend on `leaving` alone — depending
    // on the callback restarts the animation from the top each time the parent
    // re-renders, which for a stack that re-renders on every arrival means an
    // evicted card can hang on screen indefinitely.
    const exitedRef = useRef(onExited);
    exitedRef.current = onExited;

    // `leaving` is set by eviction, by the shared timer, and by her (x) alike,
    // so all three exit the same way and there is one teardown path, not three.
    useEffect(() => {
        if (!toast.leaving) {
            return;
        }
        Animated.timing(seat, {
            toValue: 0,
            duration: EXIT_MS,
            useNativeDriver: true
        }).start(({ finished }) => {
            if (finished) {
                exitedRef.current();
            }
        });
    }, [toast.leaving, seat]);

    return (
        <Animated.View
            // Touching a card stops the shared clock — she is reading it, and a
            // card that vanishes under her thumb is worse than one that lingers.
            onTouchStart={onHoldStart}
            onTouchEnd={onHoldEnd}
            onTouchCancel={onHoldEnd}
            style={[
                ss.card,
                {
                    opacity: seat,
                    transform: [
                        {
                            // Enters from above the stack; an evicted card
                            // retraces the same path, so the motion reads as one
                            // list shifting rather than cards popping.
                            translateY: seat.interpolate({
                                inputRange: [0, 1],
                                outputRange: [-8, 0]
                            })
                        },
                        {
                            scale: seat.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0.97, 1]
                            })
                        }
                    ]
                }
            ]}
            accessibilityRole="alert"
            accessibilityLabel={
                toast.aboutMedication
                    ? `Question about the ${FIELD_LABEL[toast.field]} of ${toast.aboutMedication}: ${toast.question}`
                    : `Question about the ${FIELD_LABEL[toast.field]}: ${toast.question}`
            }
        >
            <MaterialCommunityIcons
                name="help-circle-outline"
                size={18}
                color={colors.teal600}
                style={ss.icon}
            />
            <View style={ss.body}>
                <Text style={ss.about} numberOfLines={1}>
                    {toast.aboutMedication
                        ? `${toast.aboutMedication} · ${FIELD_LABEL[toast.field]}`
                        : FIELD_LABEL[toast.field]}
                </Text>
                <Text style={ss.question}>{toast.question}</Text>
                {/* Options are shown, never tapped: she answers out loud, and a
                    tappable chip here would invite her to reach for the phone
                    with a bottle in her other hand. */}
                {toast.options && toast.options.length > 0 && (
                    <Text style={ss.options} numberOfLines={1}>
                        {toast.options.join("  ·  ")}
                    </Text>
                )}
            </View>
            <Pressable
                onPress={onDismiss}
                hitSlop={12}
                style={ss.close}
                accessibilityRole="button"
                accessibilityLabel="Dismiss question"
            >
                <MaterialCommunityIcons name="close" size={16} color={colors.textTertiary} />
            </Pressable>
        </Animated.View>
    );
}

const ss = StyleSheet.create({
    stack: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.sm,
        gap: 8,
        // Above the list and the sticky bar, below any modal.
        zIndex: 50,
        elevation: 50
    },
    card: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.border,
        paddingVertical: 10,
        paddingHorizontal: 12,
        ...shadows.lg
    },
    icon: { marginTop: 1 },
    body: { flex: 1, gap: 2 },
    about: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.teal600,
        textTransform: "uppercase",
        letterSpacing: 0.4
    },
    question: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textPrimary,
        lineHeight: 18
    },
    options: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.textSecondary
    },
    close: { marginTop: 1 }
});
